/// <amd-module name="Agile/Directory/Redux/Artifacts/ArtifactReducer" />
import { IDirectoryArtifact, IDirectoryArtifactState } from "Agile/Directory/DirectoryContracts";
import { DirectoryActions, DirectoryActionTypes } from "Agile/Directory/Redux/DirectoryActions";
import { FavoriteActions, FavoriteActionTypes } from "Agile/Directory/Redux/Favorites/FavoriteActions";
import { toLookup } from "VSS/Platform/Util/Array";
import produce from "immer";

/**
 * Reducer which handles actions related to the directory artifacts
 */
export function artifactReducer<T extends IDirectoryArtifact>(
    state: IDirectoryArtifactState<T>,
    action: DirectoryActions | FavoriteActions
): IDirectoryArtifactState<T> {
    state = state || getDefaultState();

    return produce(state, draft => {
        switch (action.type) {
            case DirectoryActionTypes.InitialDataLoaded: {
                draft.artifacts = {
                    ...state.artifacts,
                    ...toLookup(action.payload.artifacts as T[], a => a.id)
                };
                draft.memberArtifactKeys = action.payload.memberKeys;
                draft.recentArtifactKeys = action.payload.recentKeys;

                if (action.payload.favorites) {
                    for (const favorite of action.payload.favorites) {
                        if (favorite.isDeleted) {
                            // create a dummy artifact. Cast as any, because we cannot know the type of T
                            draft.deletedArtifacts[favorite.artifactId] = {
                                id: favorite.artifactId,
                                name: favorite.artifactName,
                                favoriteType: favorite.artifactType
                            } as any;
                        }
                    }
                }
                break;
            }

            case DirectoryActionTypes.LoadAllDataSucceeded: {
                draft.artifacts = {
                    ...state.artifacts,
                    ...toLookup(action.payload.artifacts as T[], a => a.id)
                };
                break;
            }

            case DirectoryActionTypes.ArtifactUpdated: {
                const { artifact, artifactId } = action.payload;
                draft.artifacts[artifactId] = artifact as T;

                break;
            }

            case FavoriteActionTypes.RemoveFavoriteSuccessful: {
                const artifactId = action.payload;
                if (state.deletedArtifacts[artifactId]) {
                    delete draft.deletedArtifacts[artifactId];
                }
            }
        }
    });
}

export function getDefaultState<T extends IDirectoryArtifact>(): IDirectoryArtifactState<T> {
    return {
        artifacts: {},
        deletedArtifacts: {},
        memberArtifactKeys: [],
        recentArtifactKeys: []
    };
}
